'use client'

import { Clock3 } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { formatCourseWorkloadLabel } from '@/helpers/active-workloads'
import { getProductType } from '@/helpers/product-type'
import { limit } from '@/helpers/text'
import { baseUrl } from '@/helpers/urls'
import type { CourseType } from '@/types/course-type'
import { EnumProductType } from '@/types/enum-product-type'

function getDestinationRoute(
  productType: string,
  productId: string,
  publicUrl: string
) {
  const ProductRouteMap: Record<
    string,
    (productId: string, publicUrl: string) => string
  > = {
    [EnumProductType.COURSE]: (productId, publicUrl) =>
      `/cursos/info/${productId}/${publicUrl}`,
    [EnumProductType.WEBINAR]: (productId, publicUrl) =>
      `/cursos/info/${productId}/${publicUrl}`,
    [EnumProductType.MENTORSHIP]: (productId, publicUrl) =>
      `/cursos/info/${productId}/${publicUrl}`,
    [EnumProductType.PERSONALIZED]: (productId, publicUrl) =>
      `/cursos/info/${productId}/${publicUrl}`,
    [EnumProductType.POSTGRADUATE]: (productId, publicUrl) =>
      `/pos-graduacoes/info/${productId}/${publicUrl}`,
    [EnumProductType.TRACK]: (_, publicUrl) =>
      `/cursos/info/${productId}/${publicUrl}`,
  }

  const roulteBuilder = ProductRouteMap[productType.toLowerCase()]
  return roulteBuilder?.(productId, publicUrl)
}

interface ProductProps {
  product: CourseType
}

export default function Product({ product }: ProductProps) {
  const {
    product_type,
    external_id,
    public_url,
    reference,
    image_url,
    name,
    description,
    workloads,
  } = product

  const routeDestination: string = getDestinationRoute(
    product_type,
    external_id ?? reference,
    public_url
  )

  if (!routeDestination) {
    console.error('Erro: A rota de destino não foi gerada corretamente.')
    return null
  }

  const productType: string = getProductType(product_type)
  const workloadLabel = formatCourseWorkloadLabel(workloads)

  return (
    <Link
      className="flex w-full max-w-[270px] items-start gap-2.5 rounded-md p-2 transition-colors duration-200 md:max-w-148 md:gap-8 lg:hover:bg-gray-500/10 lg:hover:text-primary-600"
      href={routeDestination}
      title={product.name}
    >
      <div className="flex max-w-[270px] items-start gap-2.5 md:max-w-148 md:gap-8">
        <Image
          alt={name}
          className="min-h-[8.8rem] max-w-21 rounded-lg object-cover md:min-h-40 md:max-w-44"
          height="80"
          src={image_url ?? ''}
          width="150"
        />
        <div>
          <div>
            <span className="rounded-full bg-[#EAEEFD] px-2 py-1 font-medium text-primary-700 text-xs">
              {productType}
            </span>
            <h4 className="mt-1 line-clamp-1 font-medium text-primary-700 text-sm md:mt-2 md:text-lg">
              {name}
            </h4>

            <p className="mt-1 line-clamp-3 font-medium text-[#877E94] text-xss md:mt-2 md:text-sm">
              {description}
            </p>

            <div className="mt-1 flex items-center gap-2 md:mt-2">
              {product_type && product_type !== 'track' && workloadLabel && (
                <div className="flex items-center gap-1">
                  <Clock3 className="size-5 text-[#8B98D1]" />
                  <span className="font-medium text-gray-900 text-xss md:text-sm">
                    {workloadLabel}
                  </span>
                </div>
              )}

              {/* <div className="flex items-center gap-1">
                <CirclePlay className="size-5 text-[#8B98D1]" />
                <span className="text-xss font-medium text-gray-900 md:text-sm">
                  23 aulas
                </span>
              </div> */}
            </div>
          </div>
        </div>
      </div>
    </Link>
  )
}
